
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { supabase } from "@/integrations/supabase/client";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { HealthConsentField } from "@/components/HealthConsentField";
import { DataProtectionNotice } from "@/components/DataProtectionNotice";
import { TermsLabel } from "@/components/TermsLabel";
import { faltaConsentimientoSalud, textosSalud } from "@/lib/healthConsent";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const CONFIRMATION_PATHS: Record<string, string> = {
  es: "confirmacion-campamento-verano",
  en: "summer-camp-confirmation",
  pt: "confirmacao-acampamento-verao",
  fr: "confirmation-camp-ete",
};

const SummerCampRegistrationForm = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language].summerCamp;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formSchema = z.object({
    parentName: z.string().min(2, { message: "Name is required" }).max(100),
    parentEmail: z.string().email({ message: "Valid email is required" }).max(255),
    parentPhone: z.string().min(9, { message: "Valid phone is required" }).max(20),
    childName: z.string().min(2, { message: "Child name is required" }).max(100),
    childAge: z.number().int().min(3).max(12),
    childNotes: z.string().max(1000).optional(),
    weeks: z.array(z.string()).min(1, { message: "Select at least one week" }),
    needsLunch: z.boolean().default(false),
    earlyDropoff: z.boolean().default(false),
    latePickup: z.boolean().default(false),
    isGandaraMember: z.boolean().default(false),
    acceptTerms: z.boolean().refine(val => val, { message: "You must accept the terms" }),
    healthConsent: z.boolean().default(false)
  }).refine((v) => !faltaConsentimientoSalud(v.childNotes, v.healthConsent), {
    path: ["healthConsent"],
    message: textosSalud(language).error
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      parentName: "",
      parentEmail: "",
      parentPhone: "",
      childName: "",
      childAge: 3,
      childNotes: "",
      weeks: [],
      needsLunch: false,
      earlyDropoff: false,
      latePickup: false,
      isGandaraMember: false,
      acceptTerms: false,
      healthConsent: false
    }
  });

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);

    try {
      // The edge function inserts the row and sends both emails atomically.
      // If the admin email fails, it rolls back the insert so retries don't duplicate.
      const { error: invokeError } = await supabase.functions.invoke("send-summer-camp-registration", {
        body: {
          parent_name: values.parentName,
          parent_email: values.parentEmail,
          parent_phone: values.parentPhone,
          child_name: values.childName,
          child_age: values.childAge,
          child_notes: values.childNotes,
          health_consent: values.healthConsent,
          weeks: values.weeks,
          needs_lunch: values.needsLunch,
          early_dropoff: values.earlyDropoff,
          late_pickup: values.latePickup,
          is_gandara_member: values.isGandaraMember,
          language: language
        }
      });

      if (invokeError) throw invokeError;

      navigate(`/${language}/${CONFIRMATION_PATHS[language]}`);
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast.error(language === 'es' ? "Error al enviar la inscripción" :
                language === 'pt' ? "Erro ao enviar a inscrição" :
                language === 'fr' ? "Erreur lors de l'envoi de l'inscription" :
                "Error submitting registration");
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableWeeks = [
    { id: "week1", label: "22/06 - 26/06" },
    { id: "week2", label: "29/06 - 03/07" },
    { id: "week3", label: "06/07 - 10/07" },
    { id: "week4", label: "13/07 - 17/07" },
    { id: "week5", label: "20/07 - 24/07" },
    { id: "week6", label: "27/07 - 31/07" },
    { id: "week7", label: "03/08 - 07/08" },
    { id: "week8", label: "10/08 - 14/08" },
    { id: "week9", label: "17/08 - 21/08" },
    { id: "week10", label: "24/08 - 28/08" },
    { id: "week11", label: "31/08 - 04/09" },
  ];

  return (
    <div className="bg-card p-6 rounded-xl shadow-md">
      <h3 className="text-2xl font-semibold text-primary mb-6">{t.registration.form.title}</h3>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          {/* Parent Information */}
          <div>
            <h4 className="text-lg font-medium mb-4">{t.registration.form.parentInfo}</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="parentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? "Nombre completo" :
                      language === 'pt' ? "Nome completo" :
                      language === 'fr' ? "Nom complet" :
                      "Full Name"}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="María García" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="parentEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="maria@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="parentPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? "Teléfono" :
                      language === 'pt' ? "Telefone" :
                      language === 'fr' ? "Téléphone" :
                      "Phone"}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="+34 600 123 456" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Child Information */}
          <div>
            <h4 className="text-lg font-medium mb-4">{t.registration.form.childInfo}</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="childName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? "Nombre del niño/a" :
                      language === 'pt' ? "Nome da criança" :
                      language === 'fr' ? "Nom de l'enfant" :
                      "Child's Name"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="childAge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? "Edad" :
                      language === 'pt' ? "Idade" :
                      language === 'fr' ? "Âge" :
                      "Age"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={3}
                        max={12}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 3)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4">
              <FormField
                control={form.control}
                name="childNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? "Información adicional (alergias, necesidades especiales, etc.)" :
                      language === 'pt' ? "Informação adicional (alergias, necessidades especiais, etc.)" :
                      language === 'fr' ? "Informations supplémentaires (allergies, besoins spéciaux, etc.)" :
                      "Additional Information (allergies, special needs, etc.)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4">
              <HealthConsentField control={form.control} name="healthConsent" />
            </div>
          </div>

          {/* Weeks Selection */}
          <div>
            <h4 className="text-lg font-medium mb-4">{t.registration.form.weeksSelection}</h4>
            <FormField
              control={form.control}
              name="weeks"
              render={() => (
                <FormItem>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {availableWeeks.map((week) => (
                      <FormField
                        key={week.id}
                        control={form.control}
                        name="weeks"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={week.id}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(week.id)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, week.id])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== week.id
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {week.label}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Additional Options */}
          <div>
            <h4 className="text-lg font-medium mb-4">{t.registration.form.options}</h4>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="needsLunch"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {language === 'es' ? "Comedor" :
                        language === 'pt' ? "Almoço" :
                        language === 'fr' ? "Déjeuner" :
                        "Lunch"}
                      </FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="earlyDropoff"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {language === 'es' ? "Aula madruga (08:30 - 09:00)" :
                        language === 'pt' ? "Aula madrugada (08:30 - 09:00)" :
                        language === 'fr' ? "Accueil matinal (08:30 - 09:00)" :
                        "Early drop-off (08:30 - 09:00)"}
                      </FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="latePickup"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {language === 'es' ? "Aula salida (14:00 - 14:30)" :
                        language === 'pt' ? "Aula saída (14:00 - 14:30)" :
                        language === 'fr' ? "Sortie tardive (14:00 - 14:30)" :
                        "Late pickup (14:00 - 14:30)"}
                      </FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Gandara Membership */}
          <div>
            <h4 className="text-lg font-medium mb-4">{t.registration.form.membership}</h4>
            <FormField
              control={form.control}
              name="isGandaraMember"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => field.onChange(value === 'yes')}
                      defaultValue={field.value ? 'yes' : 'no'}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="yes" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {language === 'es' ? "Sí, somos miembros de Gándara" :
                          language === 'pt' ? "Sim, somos membros da Gándara" :
                          language === 'fr' ? "Oui, nous sommes membres de Gándara" :
                          "Yes, we are Gándara members"}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="no" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {language === 'es' ? "No somos miembros de Gándara" :
                          language === 'pt' ? "Não somos membros da Gándara" :
                          language === 'fr' ? "Nous ne sommes pas membres de Gándara" :
                          "We are not Gándara members"}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Terms and Conditions */}
          <FormField
            control={form.control}
            name="acceptTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    <TermsLabel />
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <DataProtectionNotice finalidad="campamento" />

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full md:w-auto"
          >
            {isSubmitting ? (
              <>
                {language === 'es' ? "Enviando..." :
                language === 'pt' ? "Enviando..." :
                language === 'fr' ? "Envoi en cours..." :
                "Submitting..."}
              </>
            ) : (
              t.registration.form.submit
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default SummerCampRegistrationForm;
